<template>
  <div>
    <div :id="id" :ref="id" :class="{disabled}" class="card card-table border-primary">
      <div v-if="disabled" class="disabled-overlay-text">{{ disabledText | capitalize }}</div>
      <div v-if="disabled" class="disabled-overlay" />

      <div v-if="title" class="card-header d-flex justify-content-between align-items-center">
        <div>
          {{ title | capitalize }}
          <small v-if="titleSmall">{{ titleSmall | capitalize }}</small>
        </div>

        <div class="d-flex justify-content-between align-items-center">
          <slot name="header-right" />

          <b-dropdown v-if="refreshBtn || columnsVisibilityBtn || (downloadBtn && list && list.length > 0)"
                      variant="link"
                      no-caret
                      right
          >
            <i slot="button-content" class="icon-more" />

            <b-dropdown-item v-if="refreshBtn" @click="load">{{ $t('grid:refresh') | capitalize }}</b-dropdown-item>
            <b-dropdown-item v-if="columnsVisibilityBtn" @click="showModalGridColumnsVisibility">{{ $t('grid:showHideColumns') | capitalize }}</b-dropdown-item>
            <b-dropdown-item v-if="manualExport && list && list.length > 0" @click="manualExport">{{ customExportLabel || $t('grid:exportPageCsv') | capitalize }}</b-dropdown-item>
            <b-dropdown-item v-if="!manualExport && (downloadBtn && list && list.length > 0)" @click="exportCSV">{{ customExportLabel || $t('grid:exportPageCsv') | capitalize }}</b-dropdown-item>
            <slot name="grid-menu" />
          </b-dropdown>
        </div>
      </div>

      <div :class="{'card-body': insidePanelBody}" class="table-responsive">
        <table :class="{'table-clickable-lines': enableClickOnRow, 'table-fixed': fixed, 'table-sm': compact}" class="table">
          <slot name="colgroup" />

          <thead>
            <tr v-for="(headerRow, indexRow) in headerRows" :key="indexRow">
              <!-- Need to keep null value to synchronize headers and values with indexCol -->
              <!-- eslint-disable-next-line -->
              <th v-for="(column, indexCol) in headerRow" v-if="column"
                  v-show="column.show"
                  :key="indexCol"
                  :class="[column.headerClass, {sortable:column.sort}]"
                  :rowspan="column.rowspan || 1"
                  :colspan="column.colspan || 1"
              >
                <span v-if="column.sort" :class="getSortOrderIcon(column.sort)" class="mr-1" @click="sort(column.sort)" />
                <span v-if="column.text" :class="{wrap: wrapHeader}" @click="column.sort ? sort(column.sort) : null">{{ column.text | capitalize }}</span>
                <slot v-else-if="column.headerSlot" :name="column.headerSlot" />
              </th>
            </tr>
          </thead>

          <tbody v-if="list && list.length > 0">
            <tr v-for="(dto, indexDto) in list"
                :key="indexDto"
                :class="{ 'highlighted': dto.highlighted }"
                @click="enableClickOnRow ? $emit('clickOnRow', dto) : null"
                @mouseover="enableMouseoverOnRow ? $emit('mouseoverOnRow', dto) : null"
                @mouseout="enableMouseoverOnRow ? $emit('mouseoutOnRow', dto) : null"
            >
              <td v-for="(column, indexCol) in columnsLeaf"
                  v-show="column.show"
                  :key="indexCol"
                  :class="[column.align ? 'text-' + column.align : '', column.class]"
              >
                <slot v-if="column.slot" :name="column.slot" :dto="dto" />
                <span v-else-if="column.formatter">{{ column.formatter(dto) }}</span>
                <span v-else>{{ dto[column.id] }}</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="list && list.length===0" class="text-center font-italic text-muted p-3">{{ (noDataMsg || $t('grid:noData')) | capitalize }}</div>
      </div>

      <div v-if="pagination" class="card-footer bg-white border-top">
        <div class="paginator form-inline">
          <div class="paginator-left">
            {{ $t('grid:rowsPerPage') | capitalize }}
            <select :id="id + 'Pagination'" v-model="pageSize" class="form-control form-control-sm" @change="goTo({pageSize: $event.target.value})">
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          <div class="paginator-center">
            <ul v-if="pagination && list && list.length > 0" class="pagination">
              <li v-if="paginatedDto.page !== 1" @click="goTo({page: 1})">
                <a :id="id + 'goToFirstPageButton'" :title="$t('grid:gotoFirstPage') | capitalize"><i class="icon-angle-double-left" /></a>
              </li>
              <li v-if="paginatedDto.page !== 1" @click="goTo({page: paginatedDto.page - 1})">
                <a :id="id + 'goToPreviousPage'" :title="$t('grid:gotoPreviousPage') | capitalize"><i class="icon-angle-left" /></a>
              </li>
              <li v-for="p in pages" :key="p" :class="[{active : p == paginatedDto.page}]" @click="goTo({page: p})">
                <a :id="id + 'goToPage'" :title="$t('grid:gotoPage', {page: p}) | capitalize" data-goto-page>{{ p }}</a>
              </li>
              <li v-if="paginatedDto.page !== paginatedDto.nbPages" @click="goTo({page: paginatedDto.page + 1})">
                <a :id="id + 'goToNextPage'" :title="$t('grid:gotoNextPage') | capitalize"><i class="icon-angle-right" /></a>
              </li>
              <li v-if="paginatedDto.page !== paginatedDto.nbPages" @click="goTo({page: paginatedDto.nbPages})">
                <a :id="id + 'goToLastPage'" :title="$t('grid:gotoLastPage') | capitalize"><i class="icon-angle-double-right" /></a>
              </li>
            </ul>
          </div>

          <div class="paginator-right text-right">
            <div v-if="pagination && list && list.length > 0">
              <span v-if="list.length > 0">
                {{ ((paginatedDto.page - 1) * paginatedDto.pageSize) + 1 }} - {{ ((paginatedDto.page - 1) * paginatedDto.pageSize) + list.length }} {{ $t('grid:of') }} {{ paginatedDto.totalCount }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal v-if="columnsVisibilityBtn"
           :id="id+'columnsVisibility'"
           v-model="showColumnsVisibility"
           :title="$t('grid:showHideColumns') | capitalize"
           :callback="validateColumnsVisibility"
    >
      <grid-columns-visibility slot="modal-body" :grid-columns="gridColumnsVisibility" />
    </modal>
  </div>
</template>

<script>
import { isNull, range, cloneDeep, each, camelCase, isUndefined, isEmpty, merge, concat, some, map, filter, indexOf, find } from 'lodash-es'
import GridColumnsVisibility from './GridColumnsVisibility'

import BDropdown from 'bootstrap-vue/es/components/dropdown/dropdown'
import BDropdownItem from 'bootstrap-vue/es/components/dropdown/dropdown-item'

export default {
  components: { GridColumnsVisibility, BDropdown, BDropdownItem },

  props: {
    // Allow column title to wrap or not
    id: { type: String, required: true },

    // Title of the grid displayed in the card header
    title: { type: String, default: '' },

    // Sub-title of the grid displayed in the card header small
    titleSmall: { type: String, default: '' },

    // Array definition of the columns
    columns: { type: Array, required: true },

    // PaginatedDto of contents
    paginatedDto: { type: Object, required: true },

    // Boolean true to paginate the list, false otherwise
    pagination: { type: Boolean, default: true },

    // Allow column title to wrap or not
    wrapHeader: { type: Boolean, default: false },

    // Boolean true to display the download button
    downloadBtn: { type: Boolean, default: true },

    // Boolean true to display the refresh button
    refreshBtn: { type: Boolean, default: true },

    // Display or not the possibility to choose columns to show/hide
    columnsVisibilityBtn: { type: Boolean, default: false },

    // Manual export function to replace default "export current page" function
    manualExport: { type: Function, default: null },

    // Manual export text for export button
    customExportLabel: { type: String, default: '' },

    // Boolean true to display the table inside a div.card-body (will not have table-bordered class)
    insidePanelBody: { type: Boolean, default: false },

    // Boolean to set the table in fixed mode, need colgroup slot to set columns width
    fixed: { type: Boolean, default: false },

    // Boolean to make tables more compact by cutting cell padding in half
    compact: { type: Boolean, default: false },

    // Add a gray overlay to disable panel
    disabled: { type: Boolean, default: false },
    // Text displayed on the disabled overlay
    disabledText: { type: String, default: 'Not available' },

    // Enable or disable event handler on rows
    // Default to false to not sent unusefull events
    enableClickOnRow: { type: Boolean, default: false },
    enableMouseoverOnRow: { type: Boolean, default: false },

    // No data message
    noDataMsg: { type: String, default: '' }
  },

  data () {
    return {
      gridColumns: this.getGridColumns(this.columns),
      gridColumnsVisibility: [],
      showColumnsVisibility: false,
      sortQuery: undefined
    }
  },

  computed: {
    headerRows () {
      return this.getHeaderRows(this.gridColumns)
    },

    /**
     * Return an array of the columns excluding the groups and keep only leafs
     */
    columnsLeaf () {
      const iRowLast = this.headerRows.length - 1
      const columnsLeaf = []

      for (let i = 0; i < this.headerRows[iRowLast].length; i++) {
        let iRow = iRowLast
        let column = this.headerRows[iRow][i]

        while (isNull(column)) {
          column = this.headerRows[iRow--][i]
        }

        columnsLeaf.push(column)
      }

      return columnsLeaf
    },

    list () {
      if (this.paginatedDto.totalCount && !this.paginatedDto.count) {
        this.goTo({ page: this.paginatedDto.nbPages })
        return []
      } else {
        return this.paginatedDto.list
      }
    },

    pages () {
      const window = 5
      const delta = Math.floor(window / 2)
      const pageMin = Math.max(1, (this.paginatedDto.page - delta) - Math.max(0, (this.paginatedDto.page + delta) - this.paginatedDto.nbPages))
      const pageMax = Math.min(this.paginatedDto.nbPages, (this.paginatedDto.page + delta) + Math.max(0, 1 - (this.paginatedDto.page - delta)))
      return range(pageMin, pageMax + 1)
    },

    pageSize: {
      // getter
      get: function () {
        return this.paginatedDto.pageSize
      },
      // setter, needed to avoid VueJS warn. The set is done by vuex in the store
      set: function () {}
    },

    gridColumnsVisibilityStateId () {
      return `gridColumnsVisibilityState-${this.id}-${this.$store.getters.authenticatedUser.login}`
    }
  },

  watch: {
    columns: {
      handler: function (columns) {
        this.gridColumns = this.getGridColumns(columns)
      },
      deep: true
    },

    $route () {
      this.load()
    },

    // Redirect to last page if the resquested page is higher than the nbPages
    paginatedDto (paginatedDto) {
      if (paginatedDto.nbPages > 0 && paginatedDto.page > paginatedDto.nbPages) this.goTo({ page: paginatedDto.nbPages })
    }
  },

  methods: {
    /**
     * Return the Grid columns configuration denpending on a columns configuration and the local storage stored state
     */
    getGridColumns (columns) {
      const strGridColumnsVisibilityState = window.localStorage.getItem(this.gridColumnsVisibilityStateId)
      const gridColumnsVisibilityState = strGridColumnsVisibilityState ? JSON.parse(strGridColumnsVisibilityState) : {}
      const newGridColumnsVisibilityState = {}

      const gridColumns = this.restoreGridColumnsVisibility(columns, gridColumnsVisibilityState, newGridColumnsVisibilityState)

      if (this.gridColumnsVisibilityStateId) {
        window.localStorage.setItem(this.gridColumnsVisibilityStateId, JSON.stringify(newGridColumnsVisibilityState))
      }

      return gridColumns
    },

    restoreGridColumnsVisibility (columns, gridColumnsVisibilityState, newGridColumnsVisibilityState, parentColumnKey) {
      const _columns = cloneDeep(columns)

      each(_columns, _column => {
        const _columnKey = (parentColumnKey ? parentColumnKey + '.' : '') + (_column.id || _column.slot || camelCase(_column.text))

        _column.show = isUndefined(_column.show) || isNull(_column.show)
          ? !isUndefined(gridColumnsVisibilityState[_columnKey])
            ? gridColumnsVisibilityState[_columnKey]
            : true
          : _column.show !== false

        if (_column.children && !isEmpty(_column.children)) {
          _column.children = this.restoreGridColumnsVisibility(_column.children, gridColumnsVisibilityState, newGridColumnsVisibilityState, _columnKey)
        }

        newGridColumnsVisibilityState[_columnKey] = _column.show
      })

      return _columns
    },

    goTo (options = {}) {
      const paginatedDto = merge({}, this.paginatedDto, options)

      // Make sure the page is not higher than the max page, but a minimum of 1...
      paginatedDto.page = Math.min(paginatedDto.page, Math.ceil(paginatedDto.totalCount / paginatedDto.pageSize)) || 1
      // Make sure the pageSize is not 0, minimum of 10...
      paginatedDto.pageSize = paginatedDto.pageSize || 10

      const routeQuery = { page: paginatedDto.page, pageSize: paginatedDto.pageSize }

      if (this.gridPageSize !== paginatedDto.pageSize) {
        // Save pageSize in localStorage for this user
        localStorage.setItem(`gridPageSize-${this.$store.getters.authenticatedUser.login}`, paginatedDto.pageSize)
        // Reset pagination to go to page 1
        paginatedDto.page = 1
        routeQuery.page = paginatedDto.page
      }

      // Update route
      this.$router.push({ path: this.$route.path, query: { ...this.$route.query, ...routeQuery } })
    },

    load () {
      this.$emit('load', { fromReload: true, page: this.$route.query.page || 1, pageSize: this.gridPageSize, sort: this.sortQuery })
    },

    /**
     * Recursively build the headers grouped by row
     */
    getHeaderRows (columns) {
      let row = []
      let columnsChildren = []

      for (let i = 0; i < columns.length; i++) {
        const column = columns[i]

        // build children columns
        if (column && column.children && column.children.length > 0) {
          columnsChildren = concat(columnsChildren, column.children)
        } else {
          columnsChildren.push(null)
        }

        row.push(column)
      }

      if (some(columnsChildren, column => { return !isNull(column) })) {
        const headerNextRows = this.getHeaderRows(columnsChildren)

        // set rowspan depending on the number of next rows
        row = map(row, column => {
          if (column) {
            if (column.children && column.children.length > 0) {
              column.rowspan = 1
              column.colspan = filter(column.children, child => { return child.show !== false }).length
              column.show = column.colspan > 0
            } else {
              column.rowspan = headerNextRows.length + 1
              column.colspan = 1
            }
          }
          return column
        })
        return concat([row], headerNextRows)
      } else {
        return [row]
      }
    },

    exportCSV () {
      this.$utils.exportCsv(this.paginatedDto.list, camelCase(this.title))
    },

    showModalGridColumnsVisibility () {
      this.gridColumnsVisibility = cloneDeep(this.gridColumns)
      this.showColumnsVisibility = true
    },

    validateColumnsVisibility () {
      this.gridColumns = this.getGridColumns(this.gridColumnsVisibility)
      this.showColumnsVisibility = false
    },

    /**
     * Return the current sort order of a field
     */
    getSortOrder (field) {
      let fields = this.paginatedDto.sort ? this.paginatedDto.sort.split(',') : []

      const found = find(fields, _field => {
        return _field.substr(1) === field
      })

      return found ? found.substr(0, 1) : ''
    },

    /**
     * Return the current sort order icon of a field
     */
    getSortOrderIcon (field) {
      switch (this.getSortOrder(field)) {
        case '+':
          return 'icon-sort-up'
        case '-':
          return 'icon-sort-down'
        default:
          return 'icon-unsorted'
      }
    },

    /**
     * Sort on a field
     */
    sort (field) {
      let fields = this.paginatedDto.sort ? this.paginatedDto.sort.split(',') : []

      // get current sort order on the field
      let operator = this.getSortOrder(field)

      // remove previous sort on the field
      if (operator) {
        fields = filter(fields, _field => {
          return _field.substr(1) !== field
        })
      }

      // add next sort order on the field
      const operatorsSteps = ['+', '-', null] // sort order steps
      operator = operatorsSteps[(indexOf(operatorsSteps, operator) + 1) % operatorsSteps.length]
      if (operator) {
        fields.unshift(operator + field)
      }

      this.sortQuery = isEmpty(fields) ? '' : fields.join(',')
      this.load()
    }
  }
}
</script>

<style scoped lang="scss">
  @import "~@/scss/variables";

  .card.card-table {
    .disabled-overlay-text {
      position: absolute;
      top: calc(50% - 20px);
      z-index: 1021;
      width: 100%;
      padding: 0 10%;
      line-height: 20px;
      color: $black;
      text-align: center;
    }

    .disabled-overlay {
      position: absolute;
      z-index: 1020;
      width: 100%;
      height: 100%;
      background: $gray-300;
      border: 1px solid $gray-500;
      border-radius: $border-radius;
      opacity: 0.8;
    }

    .table {
      margin-bottom: 0;

      // Fixed layout for word wrapping
      &.table-fixed {
        table-layout: fixed;

        td {
          word-wrap: break-word;
          white-space: inherit;
        }
      }

      // Clickable lines
      &.table-clickable-lines > tbody > tr:hover {
        cursor: pointer;
      }

      thead {
        tr {
          white-space: nowrap;

          .wrap {
            white-space: normal;
          }

          .sortable {
            span {
              cursor: pointer;
              user-select: none;

              &[class^="icon-"],
              &[class*=" icon-"] {
                font-size: 12px;
                color: $gray-400;
                text-align: right;
                vertical-align: baseline;
                cursor: pointer;
              }
            }

            &:hover {
              span {
                &[class^="icon-"],
                &[class*=" icon-"] {
                  color: $gray-600;
                }
              }
            }
          }
        }
      }

      tbody tr.highlighted {
        background-color: #00000013;
      }
    }

    .card-footer {
      .paginator {
        display: flex;
        align-items: baseline;
        justify-content: center;
        font-size: 12px;

        .paginator-left {
          flex: 3;
        }

        .paginator-center {
          flex: 6;
        }

        .paginator-right {
          flex: 3;
        }

        select.form-control {
          height: 25px;
          padding: 2px 5px;
          line-height: 25px;
          border: none;
          outline: none;
          box-shadow: none;

          &:focus,
          &:active,
          &:focus:active {
            border: none;
            outline: none;
            box-shadow: none;
          }
        }

        .pagination {
          display: flex;
          justify-content: center;
          padding: 0;
          margin: 0;
          text-align: center;
          list-style: none;

          li {
            a {
              display: inline-block;
              min-width: 25px;
              height: 25px;
              padding: 4px;
              margin: 3px 3px 3px 0;
              font-size: 12px;
              font-weight: 500;
              color: $primary;
              cursor: pointer;
              background-color: transparent;
              border: none;
              border-radius: $border-radius;
              transition: none;

              &:hover {
                background-color: $gray-300;
              }

              // Icon next/previous
              &:not([data-goto-page]) {
                padding-top: 2px;

                i {
                  margin-right: 0;
                }
              }
            }

            &.active a {
              color: $white;
              background-color: $primary;

              &:focus,
              &:hover {
                border: none;
                outline: none;
              }
            }

            &.disabled a {
              padding-top: 6px;
              margin: 0;
              cursor: auto;
              border: none;
            }
          }
        }
      }
    }
  }
</style>
